import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel } from "swiper/modules";
import { Capacitor } from "@capacitor/core";
import { Geolocation } from '@capacitor/geolocation';
import { CircularProgress } from "@mui/material";
import FlashDurations from "./FlashDurations";
import "swiper/css";
import "../styles/flashes.css";
import { countries } from "countries-list";
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  Popover,
  Typography,
  List,
  ListItem,
} from "@mui/material";
import PublicIcon from "@mui/icons-material/Public";
import FilterListIcon from "@mui/icons-material/FilterList";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import axiosInstance from "../api/axiosInstance";
import { formatDistanceToNow } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import BottomNav from './BottomNav';
import Comments from "./FlashComments";
//import { getTimeAgo } from "../utils/timeUtils";

const Flashes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = location.state?.userId;

  const [places, setPlaces] = useState([]);
  const [countriesISO, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(null);
  const [activeVerticalIndex, setActiveVerticalIndex] = useState(0);
  const [activeHorizontalIndex, setActiveHorizontalIndex] = useState(0);
  const [distanceCache, setDistanceCache] = useState({});
  const activeVideoRef = useRef(null);
  const verticalSwiperRef = useRef(null);
  const [soundOn, setSoundOn] = useState(() => {
    try {
      return localStorage.getItem('flashes_sound_on') === '1';
    } catch (_) {
      return false;
    }
  });
  const soundOnRef = useRef(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mixedPostsByPlace, setMixedPostsByPlace] = useState({});
  const seenAdsRef = useRef(new Set());
  const [taggedUsers, setTaggedUsers] = useState([]);

  useEffect(() => {
    soundOnRef.current = soundOn;
  }, [soundOn]);

  const stopActiveVideo = () => {
    if (!activeVideoRef.current) return;
    try {
      activeVideoRef.current.pause();
      activeVideoRef.current.currentTime = 0;
    } catch (_) {
      // ignore
    } finally {
      activeVideoRef.current = null;
    }
  };

  const tryPlayVideo = (videoElement) => {
    if (!videoElement) return;
    activeVideoRef.current = videoElement;

    // Autoplay with sound is blocked until a user gesture.
    // Start muted unless the user explicitly enabled sound.
    const wantSound = Boolean(soundOnRef.current);
    videoElement.muted = !wantSound;
    videoElement.volume = wantSound ? 1 : 0;

    const playPromise = videoElement.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        // If sound was requested, fall back to muted autoplay.
        if (wantSound) {
          try {
            videoElement.muted = true;
            videoElement.volume = 0;
            videoElement.play().catch(() => {});
          } catch (_) {}
        }
        console.error("Error autoplaying video:", err);
      });
    }
  };

  const toggleSound = () => {
    setSoundOn((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('flashes_sound_on', next ? '1' : '0');
      } catch (_) {}

      // Apply immediately to the currently playing video (this click is a user gesture).
      if (activeVideoRef.current) {
        try {
          activeVideoRef.current.muted = !next;
          activeVideoRef.current.volume = next ? 1 : 0;
          activeVideoRef.current.play().catch(() => {});
        } catch (_) {}
      }

      return next;
    });
  };

  const tryPlayActiveVideoInVertical = (verticalSwiper) => {
    if (!verticalSwiper?.slides?.length) return;
    const activeVerticalSlide = verticalSwiper.slides[verticalSwiper.activeIndex];
    if (!activeVerticalSlide) return;

    const horizontalEl = activeVerticalSlide.querySelector('.swiper-horizontal');
    const horizontalSwiper = horizontalEl?.swiper;
    const horizontalActiveSlide = horizontalSwiper?.slides?.[horizontalSwiper.activeIndex];
    const videoElement = horizontalActiveSlide?.querySelector('video');
    tryPlayVideo(videoElement);
  };

  const tryPlayActiveVideoInHorizontal = (horizontalSwiper) => {
    if (!horizontalSwiper?.slides?.length) return;
    // Only play when this horizontal swiper is inside the active vertical slide
    const parentVerticalSlide = horizontalSwiper.el?.closest('.swiper-slide');
    if (!parentVerticalSlide || !parentVerticalSlide.classList.contains('swiper-slide-active')) return;

    const activeSlide = horizontalSwiper.slides[horizontalSwiper.activeIndex];
    const videoElement = activeSlide?.querySelector('video');
    tryPlayVideo(videoElement);
  };

  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        if (Capacitor.getPlatform() === "web") {
          if ("geolocation" in navigator) {
            navigator.geolocation.getCurrentPosition(
              (position) => {
                console.log("User's location:", position);
                setUserLocation({
                  latitude: position.coords.latitude,
                  longitude: position.coords.longitude,
                });
                setLocationPermissionGranted(true);
              },
              (error) => {
                console.error("Error fetching user location:", error);
                setLocationPermissionGranted(false);
              }
            );
          }
          else {
            console.error("Geolocation is not supported by this browser.");
            setLocationPermissionGranted(false);
          }
        }
        else {
          const permission = await Geolocation.requestPermissions();
          if (permission.location === "granted") {
            const position = await Geolocation.getCurrentPosition();
            setUserLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
            setLocationPermissionGranted(true);
          }
          else {
            setLocationPermissionGranted(false);
          }
        }
      } catch (err) {
        console.error("Error fetching user location:", err);
      }
    };

    fetchUserLocation();
  }, []);

  const getCountryName = (isoCode) => {
    return countries[isoCode]?.name || "Unknown Country";
  };

  const fetchPlaces = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get("/fetch/flashes", {
        params: { page, limit: userId ? 5 : 1, specificUserId: userId },
      });

      if(response.data.places.length > 0) {
        setPlaces((prevPlaces) => {
          const newPlaces = response.data.places.filter(
            (newPlace) =>
              !prevPlaces.some(
                (existingPlace) =>
                  existingPlace.place_name === newPlace.place_name &&
                  existingPlace.country === newPlace.country
              )
          );
          return [...prevPlaces, ...newPlaces];
        });
        try {
          const delta = response.data.places.reduce((acc, p) => acc + (Array.isArray(p.posts) ? p.posts.length : 0), 0);
          if (delta > 0) {
            // await recordConsumption({ placement: 'flashes', organic_delta: delta });
          }
        } catch(_) {}
        setPage((prevPage) => prevPage + 1);
      }
      else {
        console.log("No more places to load.");
      }
    } catch (err) {
      console.error("Error fetching places:", err);
    } finally {
      setLoading(false);
    }
  }, [page, userId]);

  const fetchCountries = async () => {
    try {
      const response = await axiosInstance.get("/flashes/countries");
      setCountries(response.data.countries);
    } catch (err) {
      console.error("Error fetching countries:", err);
    }
  };

  const fetchCities = async (country) => {
    try {
      const response = await axiosInstance.get("/flashes/cities", {
        params: { country },
      });
      setCities(response.data.cities);
    } catch (err) {
      console.error("Error fetching cities:", err);
    }
  };

  const handleCountryChange = (e) => {
    const country = e.target.value;
    setSelectedCountry(country);
    setSelectedCity("");
    fetchCities(country);
  };

  const applyFilter = async () => {
    try {
      const response = await axiosInstance.get("/fetch/flashes", {
        params: { country: selectedCountry, city: selectedCity },
      });
      setPlaces(response.data.places);
      setIsFilterDialogOpen(false);
    } catch (err) {
      console.error("Error applying filter:", err);
    }
  };

  const handleOpenPopover = (event, tags) => {
    setAnchorEl(event.currentTarget);
    setTaggedUsers(tags);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setTaggedUsers([]);
  };

  const isPopoverOpen = Boolean(anchorEl);

  useEffect(() => {
    fetchPlaces();
    fetchCountries();
  }, [fetchPlaces]);

  // Helper: stable key for a place
  const placeKey = (p) => `${p.place_name || ''}::${p.country || ''}`;

  // Minimal session id (no PII)
  const getSessionId = () => {
    try {
      const k = 'ads_session_id';
      let v = localStorage.getItem(k);
      if (!v) { v = Math.random().toString(36).slice(2); localStorage.setItem(k, v); }
      return v;
    } catch (_) { return Math.random().toString(36).slice(2); }
  };

  // Inject our internal Sponsored ads inside each place's posts
  useEffect(() => {
    (async () => {
      const updates = {};
      for (const p of places) {
        const key = placeKey(p);
        if (mixedPostsByPlace[key]) continue; // already processed
        const posts = Array.isArray(p.posts) ? p.posts : [];
        if (!posts.length) { updates[key] = posts; continue; }
        // Ad inject disabled to avoid undefined references
        updates[key] = posts;
      }
      if (Object.keys(updates).length) {
        setMixedPostsByPlace((prev) => ({ ...prev, ...updates }));
      }
    })();
  }, [places]);

  const handleLike = async (flash_id, flash_owner, is_liked) => {
    try {
      if(is_liked) {
        await axiosInstance.delete(`/flash-likes`, { data: { flash_id } });
      }
      else {
        await axiosInstance.post(`/flash-likes`, { flash_id, flash_owner });
      }

      setPlaces((prevPlaces) =>
        prevPlaces.map((place) => ({
          ...place,
          posts: place.posts.map((post) =>
            post.flash_id === flash_id
              ? { ...post, is_liked: !is_liked }
              : post
          ),
        }))
      );
    } catch (err) {
      console.error("Error toggling like:", err);
    }
  };

  const openDrawer = (flashId) => {
    setSelectedPostId(flashId);
    setIsDrawerOpen(true);
    document.body.style.overflow = "hidden";
    document.querySelector(".bottom-nav")?.classList.add("hidden");
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedPostId(null);
    document.body.style.overflow = "";
    document.querySelector(".bottom-nav")?.classList.remove("hidden");
  };

  const resetFilters = async () => {
    setSelectedCountry("");
    setSelectedCity("");
    try {
      const response = await axiosInstance.get("/fetch/flashes", {
        params: { page: 1, limit: 5 },
      });
      setPlaces(response.data.places);
      setPage(1);
      setIsFilterDialogOpen(false);
    } catch (err) {
      console.error("Error resetting filters:", err);
    }
  };

  const redirectToGoogleMaps = (latitude, longitude) => {
    const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
    window.open(googleMapsUrl, "_blank");
  };

  const getTimeAgo = (utcTimestamp) => {
    const utcDate = fromZonedTime(new Date(utcTimestamp), "UTC");
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const localTime = toZonedTime(utcDate, userTimeZone);

    return formatDistanceToNow(localTime, { addSuffix: true }).replace(/^about /, '');
  };

  if (locationPermissionGranted === null) {
    return  (
      <>
        <div className="loading-container">
          <CircularProgress size={40} sx={{ color: '#d1d5db' }} />
        </div>
        <BottomNav />
      </>
    );
  }

  if (places.length === 0 && !loading) {
    return (
      <>
        <div className="loading-container">
          <p style={{ fontSize: "18px", color: "#d1d5db", textAlign: "center" }}>
            There are no flashes yet
          </p>
        </div>
        <BottomNav />
      </>
    );
  }

  const styles = {
    dialogTitle: {
      fontSize: "20px",
      fontWeight: "bold",
      color: "#fff",
      textAlign: "center",
      paddingBottom: "8px",
    },
    dialogContent: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "16px",
      color: "#fff",
    },
    select: {
      backgroundColor: "transparent",
      borderRadius: "8px",
    },
    dialogActions: {
      justifyContent: "space-between",
      padding: "8px 16px",
    },
    cancelButton: {
      backgroundColor: "transparent",
      color: "#fff",
      textTransform: "none",
      borderRadius: "8px",
    },
    resetButton: {
      backgroundColor: "var(--liked-red)",
      color: "#fff",
      textTransform: "none",
      borderRadius: "8px",
    },
  };

  const darkMenuItemSx = {
    backgroundColor: 'rgba(255,255,255,0.04)',
    color: '#fff',
    fontSize: '14px',
    '&.Mui-selected': {
      backgroundColor: '#000 !important',
      color: '#fff',
    },
  };

  return (
    <>
      <div style={{ marginTop: "35px" }}>
        <Swiper
          direction="vertical"
          modules={[Mousewheel]}
          mousewheel={true}
          onSwiper={(swiper) => {
            verticalSwiperRef.current = swiper;
            // Ensure the first visible video's playback starts (onSlideChange won't fire initially)
            requestAnimationFrame(() => tryPlayActiveVideoInVertical(swiper));
          }}
          onSlideChange={(swiper) => {
            setActiveVerticalIndex(swiper.activeIndex);

            stopActiveVideo();

            if (swiper.activeIndex === places.length - 1 && !loading) {
              fetchPlaces();
            }

            const activeVerticalSlide = swiper.slides[swiper.activeIndex];
            const horizontalSwiper = activeVerticalSlide.querySelector('.swiper-horizontal');
            if (horizontalSwiper) {
              const horizontalActiveIndex = horizontalSwiper.swiper.activeIndex;
              const videoElement = horizontalSwiper.swiper.slides[horizontalActiveIndex].querySelector('video');
              if (videoElement) {
                tryPlayVideo(videoElement);
              }
            }

          }}
          style={{ height: "100vh" }}
        >
          {places.map((place, placeIndex) => (
            <SwiperSlide key={placeIndex}>
              <div>
                <h2 className="flash-title">
                  {place.place_name}
                  <img
                    src={`/assets/flags/${place.country.toLowerCase()}.svg`}
                    alt={place.country}
                    className="country-flag"
                  />
                </h2>

                {locationPermissionGranted && placeIndex === activeVerticalIndex && (
                  <FlashDurations
                    latitude={place.latitude}
                    longitude={place.longitude}
                    userLocation={userLocation}
                    cache={distanceCache}
                    setCache={setDistanceCache}
                  />
                )}
                <Swiper
                  direction="horizontal"
                  modules={[Mousewheel]}
                  mousewheel={true}
                  style={{ height: "80vh" }}
                  onSwiper={(swiper) => {
                    // If this is the active vertical slide, start playback on init
                    requestAnimationFrame(() => tryPlayActiveVideoInHorizontal(swiper));
                  }}
                  onSlideChange={(swiper) => {
                    setActiveHorizontalIndex(swiper.activeIndex);

                    stopActiveVideo();
                
                    const activeSlide = swiper.slides[swiper.activeIndex];
                    const videoElement = activeSlide.querySelector('video');
                
                    if (videoElement) {
                      tryPlayVideo(videoElement);
                    }
                  }}
                >
                  {(mixedPostsByPlace[placeKey(place)] || place.posts).map((item, postIndex) => {
                    if (item && item.__kind === 'ad') {
                      // Skip rendering undefined SponsoredAdCard
                      return null;
                    }
                    const post = item;
                    const timeAgo = getTimeAgo(post.created_at);

                    return (
                      <SwiperSlide key={postIndex}>
                        <div>
                          <div className="flash-container">
                            {post.tags && post.tags.length > 0 && (
                              <span 
                                className="tag-span"
                                onClick={(e) => handleOpenPopover(e, post.tags)}
                              >
                                +{post.tags.length}
                              </span>
                            )}
                            <img
                              src={post.profile_url || "/assets/images/default_profile.png"}
                              alt={post.username}
                              onClick={(e) => (post.tags && post.tags.length > 0) ? handleOpenPopover(e, post.tags) : navigate(`/profile/${post.user_id}`)}
                              className="flash-profile-img"
                            />
                            <div className="inner-div">
                              <span 
                                className="flash-username-span"
                                onClick={() => navigate(`/profile/${post.user_id}`)}
                              >
                                {post.username}
                              </span>
                              <span className="flash-timestamp-span">{timeAgo}</span>
                            </div>
                          
                            <IconButton
                              onClick={() => setIsFilterDialogOpen(true)}
                              style={{ color: "white", position: "absolute", right: "1.25rem" }}
                            >
                              <FilterListIcon />
                            </IconButton>

                            <Popover
                              open={isPopoverOpen}
                              anchorEl={anchorEl}
                              onClose={handleClosePopover}
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                              }}
                              sx={{
                                "& .MuiPopover-paper": {
                                  backgroundColor: "#f9f9f9",
                                  boxShadow: "0 0 2px rgba(142, 142, 142, 0.1)",
                                  borderRadius: "10px",
                                  padding: "0",
                                  minWidth: "200px",
                                },
                              }}
                            >
                              <List>
                                {taggedUsers.map((tag) => (
                                  <ListItem key={tag.tag_id} onClick={() => navigate(`/profile/${tag.user_id}`)}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "#333",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <img
                                        src={tag.profile_url || "/assets/images/default_profile.png"}
                                        alt={tag.username}
                                        style={{
                                          width: "32px",
                                          height: "32px",
                                          borderRadius: "50%",
                                          objectFit: "cover",
                                          cursor: "pointer",
                                        }}
                                      />
                                      {tag.username}
                                    </Typography>
                                  </ListItem>
                                ))}
                              </List>
                            </Popover>
                          </div>

                          <div className="flash-content">
                            {post.isVideo ? (
                              <div style={{ position: 'relative' }}>
                                <video
                                  src={post.content_url}
                                  muted={!soundOn}
                                  loop
                                  playsInline
                                  preload="metadata"
                                  style={{ width: '100%', height: 'auto' }}
                                />
                                <IconButton
                                  onClick={toggleSound}
                                  aria-label={soundOn ? 'Mute video' : 'Unmute video'}
                                  sx={{
                                    position: 'absolute',
                                    right: 15,
                                    bottom: 15,
                                    backgroundColor: 'rgba(0,0,0,0.35)',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.45)' },
                                  }}
                                >
                                  {soundOn ? <VolumeUpIcon /> : <VolumeOffIcon />}
                                </IconButton>
                              </div>
                            ) : (
                              <img
                                src={post.content_url}
                                alt="Flash"
                                style={{ width: '100%', height: 'auto' }}
                              />
                            )}
                          </div>

                          <div className="flash-icons">
                            <div className="inner-flash-icons">
                              <button
                                onClick={() =>
                                  handleLike(post.flash_id, post.user_id, post.is_liked)
                                }
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  outline: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className={`fa${post.is_liked ? "s" : "r"} fa-heart`}
                                  style={{
                                    fontSize: "28px",
                                    color: post.is_liked ? "#ef4444" : "var(--text-gray-300)",
                                  }}
                                ></i>
                              </button>

                              <button
                                onClick={() => openDrawer(post.flash_id)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  outline: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <i
                                  className="far fa-comment"
                                  style={{
                                    paddingLeft: "10px",
                                    paddingRight: "10px",
                                    fontSize: "28px",
                                    color: "var(--text-gray-300)",
                                  }}
                                ></i>
                              </button>

                              <IconButton
                                onClick={() =>
                                  redirectToGoogleMaps(post.latitude, post.longitude)
                                }
                                style={{
                                  padding: "0",
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  outline: "none",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <PublicIcon
                                  style={{
                                    fontSize: "28px",
                                    color: "var(--text-gray-300)",
                                    cursor: "pointer",
                                  }}
                                />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {isDrawerOpen && selectedPostId && (
          <div>
            <Comments
              flashId={selectedPostId}
              onClose={closeDrawer}
            />
          </div>
        )}

        <Dialog 
          open={isFilterDialogOpen}
          onClose={() => setIsFilterDialogOpen(false)}
          slotProps={{
            paper: {
              sx: {
                borderRadius: "12px",
                padding: "14px",
                maxWidth: "400px",
                width: "100%",
                backgroundColor: "var(--small-dark-card)",
                color: "#fff",
                border: "1px solid rgba(255,255,255,0.08)",
              },
            },
          }}
        >
          <DialogTitle style={styles.dialogTitle}>Filter Flashes</DialogTitle>
          <DialogContent style={styles.dialogContent}>
            <Select
              fullWidth
              value={selectedCountry}
              onChange={handleCountryChange}
              displayEmpty
              variant="outlined"
              sx={{
                backgroundColor: "var(--select-dialog)",
                border: "none",
                padding: "4px 0",
                color: "#fff",
                borderRadius: '8px',
                "& .MuiSelect-select": {
                  padding: "6px 10px",
                  color: "#fff",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSvgIcon-root": {
                  color: "#fff",
                },
                "&.Mui-disabled": {
                  opacity: 0.45,
                  cursor: 'not-allowed'
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "var(--small-dark-card)",
                    color: "#fff",
                    borderRadius: "10px",
                    border: "1px solid rgba(255,255,255,0.08)",
                  },
                },
              }}
            >
              <MenuItem value="" disabled>
                Select Country
              </MenuItem>
              {countriesISO.map((country) => (
                <MenuItem key={country} value={country} sx={darkMenuItemSx}>
                  {getCountryName(country.toUpperCase())}
                </MenuItem>
              ))}
            </Select>
            <Select
              fullWidth
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              displayEmpty
              disabled={!selectedCountry}
              variant="outlined"
              sx={{
                backgroundColor: "var(--select-dialog)",
                padding: "4px 0",
                color: "#fff",
                borderRadius: '8px',
                "& .MuiSelect-select": {
                  padding: "6px 10px",
                  color: "#fff",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiSvgIcon-root": {
                  color: "#fff",
                },
                "&.Mui-disabled": {
                  opacity: 0.45,
                  cursor: 'not-allowed'
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "var(--small-dark-card)",
                    color: "#fff",
                    borderRadius: "10px",
                    border: "1px solid rgba(255,255,255,0.08)",
                  },
                },
              }}
            >
              <MenuItem value="" disabled>
                Select City
              </MenuItem>
              {cities.map((city) => (
                <MenuItem key={city} value={city} sx={darkMenuItemSx}>
                  {city}
                </MenuItem>
              ))}
            </Select>
          </DialogContent>
          <DialogActions style={styles.dialogActions}>
            <Button
              onClick={() => setIsFilterDialogOpen(false)}
              style={styles.cancelButton}
            >
              Cancel
            </Button>
            <Button onClick={resetFilters} style={styles.resetButton}>
              Reset
            </Button>
            <Button
              onClick={applyFilter}
              disabled={!selectedCity}
              aria-disabled={!selectedCity}
              sx={{
                backgroundColor: !selectedCity ? "#2f2f2f" : "var(--main-light-color)",
                color: "var(--small-dark-card)",
                textTransform: "none",
                borderRadius: "8px",
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </div>
      <BottomNav />
    </>
  );
};

export default Flashes;
